import React, { useState, useCallback, useEffect } from 'react';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Rocket, ArrowsClockwise, Copy, CheckCircle, CaretDown, CaretRight,
  ArrowLeft, Lightning, Eye, TextAa, Sparkle, X, Coins, FloppyDisk,
  DownloadSimple, Trash, Clock, PencilSimple, FolderOpen
} from '@phosphor-icons/react';
import DashboardLayout from '../components/DashboardLayout';
import BrandSettingsSummary from '../components/BrandSettingsSummary';
import ReferenceMediaUpload from '../components/ReferenceMediaUpload';
import { useUpgrade } from '../components/UpgradeSystem';
import LaunchPageEditor from '../components/LaunchPageEditor';

const API = process.env.REACT_APP_BACKEND_URL;

/* ═══ Zustand Store ═══ */
const useLaunchPageStore = create(
  persist(
    (set) => ({
      businessName: '', businessDescription: '', targetAudience: '',
      mainOffer: '', topBenefits: ['', '', ''], pageType: 'product_saas', tone: 'Professional',
      sections: null, pageId: null, pageName: '', loading: false, regeneratingSection: null,
      activeTab: 'copy', hasSeenIntro: false, savedPages: [], showLibrary: false,
      setField: (k, v) => set({ [k]: v }),
      setBenefit: (i, v) => set(s => {
        const b = [...s.topBenefits];
        b[i] = v;
        return { topBenefits: b };
      }),
      setSections: (sections, pageId) => set({ sections, pageId, loading: false }),
      updateSection: (key, content) => set(s => ({
        sections: s.sections ? { ...s.sections, [key]: content } : null
      })),
      setLoading: (loading) => set({ loading }),
      setRegenerating: (key) => set({ regeneratingSection: key }),
      setTab: (tab) => set({ activeTab: tab }),
      dismissIntro: () => set({ hasSeenIntro: true }),
      setSavedPages: (pages) => set({ savedPages: pages }),
      reset: () => set({ sections: null, pageId: null, pageName: '', loading: false, regeneratingSection: null }),
    }),
    {
      name: 'scalitos-launch-page',
      partialize: (s) => ({
        businessName: s.businessName, businessDescription: s.businessDescription,
        targetAudience: s.targetAudience, mainOffer: s.mainOffer,
        topBenefits: s.topBenefits, pageType: s.pageType, tone: s.tone,
        sections: s.sections, pageId: s.pageId, pageName: s.pageName,
        activeTab: s.activeTab, hasSeenIntro: s.hasSeenIntro,
      }),
    }
  )
);

const PAGE_TYPES = [
  { id: 'product_saas', label: 'Product / SaaS' },
  { id: 'service_agency', label: 'Service / Agency' },
  { id: 'personal_brand', label: 'Personal Brand' },
];

const TONES = ['Professional', 'Casual', 'Bold', 'Friendly', 'Luxurious', 'Inspirational', 'Witty', 'Playful'];

const SECTION_LABELS = {
  hero: 'Hero', problem: 'Problem', solution: 'Solution',
  features: 'Features', social_proof: 'Social Proof', cta: 'Call to Action', faq: 'FAQ'
};

/* ═══ Credit Confirmation Modal ═══ */
const CreditModal = ({ credits, action, onConfirm, onCancel }) => (
  <motion.div
    initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
    className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
    onClick={onCancel}
  >
    <motion.div
      initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
      className="bg-[#1a1a1a] border border-white/10 rounded-2xl p-6 max-w-sm w-full mx-4"
      onClick={e => e.stopPropagation()}
    >
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-xl bg-[#c8f060]/10 flex items-center justify-center">
          <Coins size={20} className="text-[#c8f060]" />
        </div>
        <div>
          <h3 className="font-medium text-white">Credit Usage</h3>
          <p className="text-sm text-zinc-400">{action}</p>
        </div>
      </div>
      <p className="text-zinc-300 text-sm mb-6">
        This will use <span className="text-[#c8f060] font-semibold">{credits} credit{credits > 1 ? 's' : ''}</span>. Continue?
      </p>
      <div className="flex gap-3">
        <button onClick={onCancel} className="flex-1 px-4 py-2.5 rounded-xl border border-white/10 text-zinc-400 hover:bg-white/5 text-sm transition-colors">
          Cancel
        </button>
        <button onClick={onConfirm} data-testid="confirm-credit-btn" className="flex-1 px-4 py-2.5 rounded-xl bg-[#c8f060] text-black font-medium text-sm hover:bg-[#d4f57a] transition-colors">
          Use {credits} Credit{credits > 1 ? 's' : ''}
        </button>
      </div>
    </motion.div>
  </motion.div>
);

/* ═══ First-Time Intro ═══ */
const IntroOverlay = ({ onStart }) => (
  <motion.div
    initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
    className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm"
  >
    <motion.div
      initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.1 }}
      className="bg-[#141414] border border-white/10 rounded-2xl p-8 max-w-md w-full mx-4 text-center"
    >
      <div className="w-14 h-14 rounded-2xl bg-[#c8f060]/10 flex items-center justify-center mx-auto mb-5">
        <Rocket size={28} className="text-[#c8f060]" />
      </div>
      <h2 className="text-xl font-medium font-['Instrument_Serif',serif] text-white mb-3">
        Create your first landing page
      </h2>
      <p className="text-zinc-400 text-sm mb-6 leading-relaxed">
        Fill in a few details and we'll generate a complete landing page for your business.
      </p>
      <div className="space-y-3 text-left mb-8">
        {['Structured copy for every section', 'Ready to copy and use anywhere', 'Live visual preview'].map((item, i) => (
          <div key={i} className="flex items-center gap-3 text-sm text-zinc-300">
            <CheckCircle size={16} className="text-[#c8f060] shrink-0" />
            <span>{item}</span>
          </div>
        ))}
      </div>
      <button
        onClick={onStart}
        data-testid="intro-start-btn"
        className="w-full py-3 bg-[#c8f060] text-black font-medium rounded-xl hover:bg-[#d4f57a] transition-colors"
      >
        Start
      </button>
    </motion.div>
  </motion.div>
);

/* ═══ Section Copy Card ═══ */
const SectionCopyCard = ({ sectionKey, content, onRegenerate, isRegenerating }) => {
  const [copiedField, setCopiedField] = useState(null);

  const copyText = (text, field) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const CopyBtn = ({ text, field }) => (
    <button
      onClick={() => copyText(text, field)}
      className="p-1.5 text-zinc-500 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
      title="Copy"
    >
      {copiedField === field ? <CheckCircle size={14} className="text-[#c8f060]" /> : <Copy size={14} />}
    </button>
  );

  const renderContent = () => {
    if (!content) return <p className="text-zinc-500 text-sm">No content generated</p>;

    switch (sectionKey) {
      case 'hero':
        return (
          <div className="space-y-3">
            <div className="flex justify-between items-start gap-2">
              <div>
                <span className="text-[10px] uppercase tracking-wider text-zinc-500">Headline</span>
                <p className="text-white font-medium text-lg">{content.headline}</p>
              </div>
              <CopyBtn text={content.headline} field="headline" />
            </div>
            <div className="flex justify-between items-start gap-2">
              <div>
                <span className="text-[10px] uppercase tracking-wider text-zinc-500">Subheadline</span>
                <p className="text-zinc-300 text-sm">{content.subheadline}</p>
              </div>
              <CopyBtn text={content.subheadline} field="subheadline" />
            </div>
            <div className="flex justify-between items-center gap-2">
              <div>
                <span className="text-[10px] uppercase tracking-wider text-zinc-500">CTA</span>
                <p className="text-[#c8f060] font-medium">{content.cta_text}</p>
              </div>
              <CopyBtn text={content.cta_text} field="cta" />
            </div>
          </div>
        );

      case 'problem':
        return (
          <div className="space-y-3">
            <div className="flex justify-between items-start gap-2">
              <div>
                <span className="text-[10px] uppercase tracking-wider text-zinc-500">Headline</span>
                <p className="text-white font-medium">{content.headline}</p>
              </div>
              <CopyBtn text={content.headline} field="p-headline" />
            </div>
            <div>
              <span className="text-[10px] uppercase tracking-wider text-zinc-500">Description</span>
              <p className="text-zinc-300 text-sm mt-1">{content.description}</p>
            </div>
            {content.pain_points?.length > 0 && (
              <div>
                <span className="text-[10px] uppercase tracking-wider text-zinc-500">Pain Points</span>
                <ul className="mt-1 space-y-1">
                  {content.pain_points.map((p, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-zinc-300">
                      <span className="text-red-400 mt-0.5">-</span>{p}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        );

      case 'solution':
        return (
          <div className="space-y-3">
            <div className="flex justify-between items-start gap-2">
              <div>
                <span className="text-[10px] uppercase tracking-wider text-zinc-500">Headline</span>
                <p className="text-white font-medium">{content.headline}</p>
              </div>
              <CopyBtn text={content.headline} field="s-headline" />
            </div>
            <div>
              <span className="text-[10px] uppercase tracking-wider text-zinc-500">Description</span>
              <p className="text-zinc-300 text-sm mt-1">{content.description}</p>
            </div>
            {content.key_differentiator && (
              <div className="p-3 bg-[#c8f060]/5 border border-[#c8f060]/10 rounded-lg">
                <span className="text-[10px] uppercase tracking-wider text-[#c8f060]/70">Key Differentiator</span>
                <p className="text-zinc-200 text-sm mt-1">{content.key_differentiator}</p>
              </div>
            )}
          </div>
        );

      case 'features':
        return (
          <div className="space-y-3">
            <div className="flex justify-between items-start gap-2">
              <div>
                <span className="text-[10px] uppercase tracking-wider text-zinc-500">Headline</span>
                <p className="text-white font-medium">{content.headline}</p>
              </div>
              <CopyBtn text={content.headline} field="f-headline" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {(content.items || []).map((item, i) => (
                <div key={i} className="p-3 bg-zinc-900/50 rounded-lg">
                  <p className="text-white text-sm font-medium">{item.title}</p>
                  <p className="text-zinc-400 text-xs mt-1">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        );

      case 'social_proof':
        return (
          <div className="space-y-3">
            {content.testimonial && (
              <div className="p-4 bg-zinc-900/50 rounded-lg border-l-2 border-[#c8f060]/30">
                <p className="text-zinc-200 text-sm italic">"{content.testimonial.quote}"</p>
                <div className="mt-2 flex items-center gap-2">
                  <p className="text-white text-xs font-medium">{content.testimonial.author}</p>
                  <span className="text-zinc-600 text-xs">-</span>
                  <p className="text-zinc-400 text-xs">{content.testimonial.role}</p>
                </div>
                {content.testimonial.metric && (
                  <p className="text-[#c8f060] text-xs mt-1">{content.testimonial.metric}</p>
                )}
              </div>
            )}
            {content.stats?.length > 0 && (
              <div className="grid grid-cols-3 gap-2">
                {content.stats.map((s, i) => (
                  <div key={i} className="text-center p-2 bg-zinc-900/50 rounded-lg">
                    <p className="text-white font-semibold text-lg">{s.value}</p>
                    <p className="text-zinc-500 text-xs">{s.label}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        );

      case 'cta':
        return (
          <div className="space-y-3">
            <div className="flex justify-between items-start gap-2">
              <div>
                <span className="text-[10px] uppercase tracking-wider text-zinc-500">Headline</span>
                <p className="text-white font-medium">{content.headline}</p>
              </div>
              <CopyBtn text={content.headline} field="cta-headline" />
            </div>
            <div>
              <span className="text-[10px] uppercase tracking-wider text-zinc-500">Subheadline</span>
              <p className="text-zinc-300 text-sm mt-1">{content.subheadline}</p>
            </div>
            <div className="flex justify-between items-center gap-2">
              <div>
                <span className="text-[10px] uppercase tracking-wider text-zinc-500">Button</span>
                <p className="text-[#c8f060] font-medium">{content.button_text}</p>
              </div>
              <CopyBtn text={content.button_text} field="cta-btn" />
            </div>
            {content.guarantee && (
              <p className="text-zinc-500 text-xs">{content.guarantee}</p>
            )}
          </div>
        );

      case 'faq':
        return (
          <div className="space-y-2">
            {(content.items || []).map((faq, i) => (
              <FAQItem key={i} q={faq.question} a={faq.answer} />
            ))}
          </div>
        );

      default:
        return <pre className="text-zinc-400 text-xs overflow-auto">{JSON.stringify(content, null, 2)}</pre>;
    }
  };

  return (
    <div data-testid={`section-card-${sectionKey}`} className="bg-[#141414] border border-white/[0.07] rounded-2xl overflow-hidden">
      <div className="flex items-center justify-between px-5 py-3 border-b border-white/[0.05]">
        <span className="text-xs uppercase tracking-wider text-zinc-400 font-medium">{SECTION_LABELS[sectionKey]}</span>
        <button
          onClick={onRegenerate}
          disabled={isRegenerating}
          data-testid={`regen-${sectionKey}-btn`}
          className="flex items-center gap-1.5 text-xs text-zinc-500 hover:text-white px-2.5 py-1.5 rounded-lg hover:bg-white/5 transition-colors disabled:opacity-40"
        >
          <ArrowsClockwise size={12} className={isRegenerating ? 'animate-spin' : ''} />
          {isRegenerating ? 'Regenerating...' : 'Regenerate (1 credit)'}
        </button>
      </div>
      <div className="p-5">
        {renderContent()}
      </div>
    </div>
  );
};

/* ═══ FAQ Accordion ═══ */
const FAQItem = ({ q, a }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-white/5 rounded-lg overflow-hidden">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between p-3 text-left hover:bg-white/[0.03] transition-colors">
        <span className="text-sm text-white font-medium">{q}</span>
        {open ? <CaretDown size={14} className="text-zinc-500" /> : <CaretRight size={14} className="text-zinc-500" />}
      </button>
      <AnimatePresence>
        {open && (
          <motion.div initial={{ height: 0 }} animate={{ height: 'auto' }} exit={{ height: 0 }} className="overflow-hidden">
            <p className="px-3 pb-3 text-sm text-zinc-400">{a}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

/* ═══ Preview Tab ═══ */
const PreviewTab = ({ sections }) => {
  if (!sections) return null;
  const { hero, problem, solution, features, social_proof, cta, faq } = sections;

  return (
    <div data-testid="launch-page-preview" className="bg-white rounded-2xl overflow-hidden text-gray-900 max-w-3xl mx-auto shadow-2xl">
      {/* Hero */}
      <div className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white px-8 py-20 text-center">
        <h1 className="text-3xl sm:text-4xl font-bold mb-4 tracking-tight">{hero?.headline}</h1>
        <p className="text-gray-300 text-base sm:text-lg max-w-xl mx-auto mb-8">{hero?.subheadline}</p>
        <button className="px-8 py-3 bg-[#c8f060] text-gray-900 font-semibold rounded-full text-sm hover:bg-[#d4f57a] transition-colors">
          {hero?.cta_text}
        </button>
      </div>

      {/* Problem */}
      <div className="px-8 py-16 bg-gray-50 text-center">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">{problem?.headline}</h2>
        <p className="text-gray-600 max-w-xl mx-auto mb-8">{problem?.description}</p>
        {problem?.pain_points?.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-2xl mx-auto">
            {problem.pain_points.map((p, i) => (
              <div key={i} className="p-4 bg-white rounded-xl shadow-sm border border-gray-100">
                <p className="text-sm text-gray-700">{p}</p>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Solution */}
      <div className="px-8 py-16 text-center">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">{solution?.headline}</h2>
        <p className="text-gray-600 max-w-xl mx-auto mb-4">{solution?.description}</p>
        {solution?.key_differentiator && (
          <p className="text-sm text-[#6b8e23] font-medium max-w-md mx-auto">{solution.key_differentiator}</p>
        )}
      </div>

      {/* Features */}
      <div className="px-8 py-16 bg-gray-50">
        <h2 className="text-2xl font-bold text-gray-900 mb-8 text-center">{features?.headline}</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 max-w-2xl mx-auto">
          {(features?.items || []).map((f, i) => (
            <div key={i} className="p-5 bg-white rounded-xl shadow-sm border border-gray-100">
              <h3 className="font-semibold text-gray-900 mb-1">{f.title}</h3>
              <p className="text-sm text-gray-600">{f.description}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Social Proof */}
      <div className="px-8 py-16 text-center">
        <h2 className="text-2xl font-bold text-gray-900 mb-8">{social_proof?.headline}</h2>
        {social_proof?.testimonial && (
          <div className="max-w-xl mx-auto mb-8">
            <p className="text-gray-700 text-lg italic mb-3">"{social_proof.testimonial.quote}"</p>
            <p className="text-sm font-semibold text-gray-900">{social_proof.testimonial.author}</p>
            <p className="text-xs text-gray-500">{social_proof.testimonial.role}</p>
            {social_proof.testimonial.metric && (
              <p className="text-xs text-[#6b8e23] font-medium mt-1">{social_proof.testimonial.metric}</p>
            )}
          </div>
        )}
        {social_proof?.stats?.length > 0 && (
          <div className="grid grid-cols-3 gap-4 max-w-md mx-auto">
            {social_proof.stats.map((s, i) => (
              <div key={i}>
                <p className="text-2xl font-bold text-gray-900">{s.value}</p>
                <p className="text-xs text-gray-500">{s.label}</p>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* CTA */}
      <div className="px-8 py-16 bg-gray-900 text-white text-center">
        <h2 className="text-2xl font-bold mb-3">{cta?.headline}</h2>
        <p className="text-gray-400 max-w-md mx-auto mb-6">{cta?.subheadline}</p>
        <button className="px-8 py-3 bg-[#c8f060] text-gray-900 font-semibold rounded-full text-sm hover:bg-[#d4f57a] transition-colors mb-3">
          {cta?.button_text}
        </button>
        {cta?.guarantee && (
          <p className="text-xs text-gray-500">{cta.guarantee}</p>
        )}
      </div>

      {/* FAQ */}
      <div className="px-8 py-16">
        <h2 className="text-2xl font-bold text-gray-900 mb-8 text-center">{faq?.headline}</h2>
        <div className="max-w-xl mx-auto space-y-3">
          {(faq?.items || []).map((item, i) => (
            <div key={i} className="border border-gray-200 rounded-xl p-4">
              <p className="font-medium text-gray-900 text-sm">{item.question}</p>
              <p className="text-gray-600 text-sm mt-2">{item.answer}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Footer */}
      <div className="px-8 py-6 bg-gray-100 text-center border-t border-gray-200">
        <p className="text-xs text-gray-500">Built with ScalitOS</p>
      </div>
    </div>
  );
};


/* ═══ Saved Page Card ═══ */
const SavedPageCard = ({ page, onLoad, onDelete, onRename }) => {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(page.name || page.input?.business_name || 'Untitled');

  const handleRename = () => {
    if (name.trim()) {
      onRename(page.page_id, name.trim());
      setEditing(false);
    }
  };

  return (
    <div className="p-4 rounded-xl border border-white/[0.05] bg-white/[0.02] hover:bg-white/[0.04] transition-colors group">
      <div className="flex items-start justify-between gap-2 mb-2">
        {editing ? (
          <input
            autoFocus value={name} onChange={e => setName(e.target.value)}
            onBlur={handleRename} onKeyDown={e => e.key === 'Enter' && handleRename()}
            className="text-sm font-medium text-white bg-zinc-900 border border-white/10 rounded px-2 py-1 w-full"
          />
        ) : (
          <p className="text-sm font-medium text-white truncate">{page.name || page.input?.business_name || 'Untitled'}</p>
        )}
      </div>
      {page.sections?.hero?.headline && (
        <p className="text-xs text-zinc-500 truncate mb-3">{page.sections.hero.headline}</p>
      )}
      <div className="flex items-center justify-between">
        <p className="text-[10px] text-zinc-600">
          {page.created_at ? new Date(page.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : ''}
        </p>
        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <button onClick={() => setEditing(true)} className="p-1.5 rounded-lg hover:bg-white/10" title="Rename">
            <PencilSimple size={12} className="text-zinc-500" />
          </button>
          <button onClick={() => onLoad(page)} data-testid={`load-page-${page.page_id}`}
            className="p-1.5 rounded-lg hover:bg-white/10" title="Load">
            <FolderOpen size={12} className="text-zinc-500" />
          </button>
          <button onClick={() => onDelete(page.page_id)} className="p-1.5 rounded-lg hover:bg-red-500/10" title="Delete">
            <Trash size={12} className="text-red-400/50" />
          </button>
        </div>
      </div>
    </div>
  );
};


/* ═══ Main Page ═══ */
export const LaunchPageGenerator = () => {
  const store = useLaunchPageStore();
  const { showUpgradeModal } = useUpgrade();
  const [creditModal, setCreditModal] = useState(null);
  const [copiedAll, setCopiedAll] = useState(false);
  const [confirmedCredits, setConfirmedCredits] = useState(false);
  // Zero Friction: progressive disclosure for business name / audience / offer / benefits / type / tone
  const [showAdvanced, setShowAdvanced] = useState(false);
  // Page goal pills (drive a sensible default pageType behind the scenes)
  const [pageGoal, setPageGoal] = useState('leads');

  const handleBrandProfileLoad = (bp) => {
    if (!bp) return;
    if (bp.brand_name && !store.businessName) store.setField('businessName', bp.brand_name);
    if (bp.target_audience && !store.targetAudience) store.setField('targetAudience', bp.target_audience);
    if (bp.tone_of_voice) {
      const matched = TONES.find(t => t.toLowerCase() === String(bp.tone_of_voice).toLowerCase());
      if (matched && !store.tone) store.setField('tone', matched);
    }
  };

  const getPayload = useCallback(() => ({
    business_name: store.businessName,
    business_description: store.businessDescription,
    target_audience: store.targetAudience,
    main_offer: store.mainOffer,
    top_benefits: store.topBenefits.filter(b => b.trim()),
    page_type: store.pageType,
    tone: store.tone,
  }), [store.businessName, store.businessDescription, store.targetAudience, store.mainOffer, store.topBenefits, store.pageType, store.tone]);

  // Zero Friction: only "What's this page for?" is strictly required.
  const isFormValid = store.businessDescription.trim().length >= 4;

  const handleGenerate = async (creditCost = 10, isRegen = false) => {
    if (!isFormValid) return;

    if (!confirmedCredits) {
      setCreditModal({
        credits: creditCost,
        action: isRegen ? 'Regenerate entire landing page' : 'Generate a complete landing page',
        onConfirm: () => {
          setCreditModal(null);
          setConfirmedCredits(true);
          doGenerate(creditCost, isRegen);
        }
      });
      return;
    }
    doGenerate(creditCost, isRegen);
  };

  const doGenerate = async (creditCost, isRegen) => {
    store.setLoading(true);
    try {
      const endpoint = isRegen ? '/api/launch-page/regenerate' : '/api/launch-page/generate';
      const { data } = await axios.post(`${API}${endpoint}`, getPayload());
      store.setSections(data.sections, data.page_id || store.pageId);
      toast.success(isRegen
        ? `Page regenerated \u2022 ${data.credits_used} credits used`
        : `Your landing page is ready \u2022 ${data.credits_used} credits used`
      );
    } catch (err) {
      store.setLoading(false);
      const status = err.response?.status;
      if (status === 402) {
        showUpgradeModal?.('credits');
        toast.error('Not enough credits');
      } else if (status === 429) {
        toast.error('Daily limit reached. Try again tomorrow.');
      } else {
        toast.error('Generation failed. Please try again.');
      }
    }
  };

  const handleRegenerateSection = async (sectionKey) => {
    store.setRegenerating(sectionKey);
    try {
      const { data } = await axios.post(`${API}/api/launch-page/regenerate-section`, {
        ...getPayload(),
        section_key: sectionKey,
        current_content: store.sections?.[sectionKey] || null,
      });
      store.updateSection(sectionKey, data.content);
      store.setRegenerating(null);
      toast.success(`1 credit used \u2022 ${SECTION_LABELS[sectionKey]} updated`);
    } catch (err) {
      store.setRegenerating(null);
      const status = err.response?.status;
      if (status === 402) {
        showUpgradeModal?.('credits');
        toast.error('Not enough credits');
      } else {
        toast.error('Section regeneration failed');
      }
    }
  };

  const copyAllSections = () => {
    if (!store.sections) return;
    const parts = [];
    const s = store.sections;
    if (s.hero) parts.push(`# HERO\n\nHeadline: ${s.hero.headline}\nSubheadline: ${s.hero.subheadline}\nCTA: ${s.hero.cta_text}`);
    if (s.problem) {
      parts.push(`# PROBLEM\n\n${s.problem.headline}\n${s.problem.description}\n\nPain Points:\n${(s.problem.pain_points || []).map(p => `- ${p}`).join('\n')}`);
    }
    if (s.solution) parts.push(`# SOLUTION\n\n${s.solution.headline}\n${s.solution.description}\n\n${s.solution.key_differentiator || ''}`);
    if (s.features) {
      parts.push(`# FEATURES\n\n${s.features.headline}\n\n${(s.features.items || []).map(f => `- ${f.title}: ${f.description}`).join('\n')}`);
    }
    if (s.social_proof) {
      let sp = `# SOCIAL PROOF\n\n${s.social_proof.headline}`;
      if (s.social_proof.testimonial) sp += `\n\n"${s.social_proof.testimonial.quote}"\n- ${s.social_proof.testimonial.author}, ${s.social_proof.testimonial.role}`;
      parts.push(sp);
    }
    if (s.cta) parts.push(`# CTA\n\n${s.cta.headline}\n${s.cta.subheadline}\nButton: ${s.cta.button_text}\n${s.cta.guarantee || ''}`);
    if (s.faq) {
      parts.push(`# FAQ\n\n${(s.faq.items || []).map(f => `Q: ${f.question}\nA: ${f.answer}`).join('\n\n')}`);
    }
    navigator.clipboard.writeText(parts.join('\n\n---\n\n'));
    setCopiedAll(true);
    toast.success('All sections copied to clipboard');
    setTimeout(() => setCopiedAll(false), 2000);
  };

  /* ── Saved Pages Library ── */
  const loadSavedPages = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/launch-page/saved`);
      store.setSavedPages(data.pages || []);
    } catch {}
  }, []);

  useEffect(() => { loadSavedPages(); }, [loadSavedPages]);

  const loadPage = (page) => {
    store.setField('businessName', page.input?.business_name || '');
    store.setField('businessDescription', page.input?.business_description || '');
    store.setField('targetAudience', page.input?.target_audience || '');
    store.setField('mainOffer', page.input?.main_offer || '');
    store.setField('topBenefits', page.input?.top_benefits?.length >= 3 ? page.input.top_benefits : [...(page.input?.top_benefits || []), '', '', ''].slice(0, 3));
    store.setField('pageType', page.input?.page_type || 'product_saas');
    store.setField('tone', page.input?.tone || 'Professional');
    store.setField('pageName', page.name || page.input?.business_name || '');
    store.setSections(page.sections, page.page_id);
    store.setField('showLibrary', false);
  };

  const deletePage = async (pageId) => {
    try {
      await axios.delete(`${API}/api/launch-page/${pageId}`);
      store.setSavedPages(store.savedPages.filter(p => p.page_id !== pageId));
      if (store.pageId === pageId) store.reset();
      toast.success('Page deleted');
    } catch { toast.error('Failed to delete'); }
  };

  const renamePage = async (pageId, name) => {
    try {
      await axios.put(`${API}/api/launch-page/${pageId}/rename`, { name });
      loadSavedPages();
      toast.success('Renamed');
    } catch { toast.error('Failed to rename'); }
  };

  /* ── Download HTML ── */
  const [publishing, setPublishing] = useState(false);
  const [publishedUrl, setPublishedUrl] = useState(null);

  const publishPage = async () => {
    if (!store.sections) {
      toast.error('Generate a page first');
      return;
    }
    setPublishing(true);
    try {
      // If no pageId, save first
      let pageId = store.pageId;
      if (!pageId) {
        const { data: genData } = await axios.post(`${API}/api/launch-page/generate`, getPayload());
        pageId = genData.page_id;
        store.setSections(genData.sections, pageId);
      }
      try {
        const { data } = await axios.post(`${API}/api/launch-page/${pageId}/publish`, {});
        setPublishedUrl(data.url);
        toast.success('Page published!');
        loadSavedPages();
      } catch (err) {
        // If 404, the stored pageId is stale (belongs to different user). Re-generate to get a valid page.
        if (err.response?.status === 404) {
          const { data: genData } = await axios.post(`${API}/api/launch-page/generate`, getPayload());
          store.setSections(genData.sections, genData.page_id);
          const { data } = await axios.post(`${API}/api/launch-page/${genData.page_id}/publish`, {});
          setPublishedUrl(data.url);
          toast.success('Page published!');
          loadSavedPages();
        } else {
          throw err;
        }
      }
    } catch (err) {
      toast.error(typeof (err.response?.data?.detail) === 'string' ? err.response.data.detail : (err.response?.data?.detail?.message || 'Publish failed'));
    }
    setPublishing(false);
  };

  const unpublishPage = async () => {
    if (!store.pageId) return;
    try {
      await axios.post(`${API}/api/launch-page/${store.pageId}/unpublish`);
      setPublishedUrl(null);
      toast.success('Page unpublished');
      loadSavedPages();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Unpublish failed');
    }
  };

  // Check if current page is published
  useEffect(() => {
    if (store.pageId && store.savedPages.length > 0) {
      const current = store.savedPages.find(p => p.page_id === store.pageId);
      if (current?.published_url) {
        setPublishedUrl(current.published_url);
      } else {
        setPublishedUrl(null);
      }
    }
  }, [store.pageId, store.savedPages]);

  const downloadHTML = () => {
    if (!store.sections) return;
    const s = store.sections;
    const name = store.pageName || store.businessName || 'Landing Page';
    const html = `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8"><meta name="viewport" content="width=device-width, initial-scale=1.0">
<title>${name}</title>
<link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap" rel="stylesheet">
<style>
*{margin:0;padding:0;box-sizing:border-box}body{font-family:'Inter',sans-serif;color:#1a1a1a;line-height:1.6}
.hero{background:linear-gradient(135deg,#111 0%,#1a1a2e 100%);color:#fff;padding:80px 24px;text-align:center}
.hero h1{font-size:clamp(28px,5vw,48px);font-weight:700;margin-bottom:16px;letter-spacing:-0.02em}
.hero p{font-size:18px;color:#ccc;max-width:560px;margin:0 auto 32px}
.btn{display:inline-block;padding:14px 32px;background:#c8f060;color:#111;font-weight:600;border-radius:999px;text-decoration:none;font-size:15px}
.section{padding:64px 24px;max-width:800px;margin:0 auto}
.section.alt{background:#f9f9f9;max-width:100%}.section.alt>*{max-width:800px;margin-left:auto;margin-right:auto}
.section h2{font-size:28px;font-weight:700;margin-bottom:16px;text-align:center}
.section p{color:#555;text-align:center}
.grid{display:grid;grid-template-columns:repeat(auto-fit,minmax(220px,1fr));gap:20px;margin-top:24px}
.card{background:#fff;border:1px solid #eee;border-radius:12px;padding:24px}
.card h3{font-weight:600;margin-bottom:6px}.card p{text-align:left;font-size:14px}
.quote{font-style:italic;font-size:18px;color:#333;max-width:560px;margin:0 auto 16px;text-align:center}
.stats{display:flex;justify-content:center;gap:40px;margin-top:24px}
.stat{text-align:center}.stat .val{font-size:28px;font-weight:700}.stat .lbl{font-size:12px;color:#999}
.cta-sec{background:#111;color:#fff;padding:64px 24px;text-align:center}
.cta-sec h2{font-size:28px;font-weight:700;margin-bottom:8px;color:#fff}
.cta-sec p{color:#888;max-width:480px;margin:0 auto 24px}
.cta-sec .guarantee{font-size:12px;color:#666;margin-top:12px}
.faq{max-width:640px;margin:24px auto 0}.faq-item{border:1px solid #eee;border-radius:12px;padding:16px;margin-bottom:12px}
.faq-item h4{font-weight:600;margin-bottom:6px}.faq-item p{font-size:14px;color:#555;text-align:left}
.footer{background:#f5f5f5;padding:24px;text-align:center;font-size:12px;color:#aaa;border-top:1px solid #eee}
.pain{display:grid;grid-template-columns:repeat(auto-fit,minmax(200px,1fr));gap:12px;margin-top:20px}
.pain-item{background:#fff;border:1px solid #eee;border-radius:8px;padding:16px;font-size:14px;color:#555;text-align:center}
.diff{background:#f0f8e0;border:1px solid #d4e8a0;border-radius:8px;padding:16px;margin-top:16px;font-size:14px;color:#4a6e10;text-align:center}
</style>
</head>
<body>
<div class="hero">
<h1>${s.hero?.headline || ''}</h1>
<p>${s.hero?.subheadline || ''}</p>
<a href="#" class="btn">${s.hero?.cta_text || 'Get Started'}</a>
</div>
<div class="section alt">
<h2>${s.problem?.headline || ''}</h2>
<p>${s.problem?.description || ''}</p>
${s.problem?.pain_points?.length ? `<div class="pain">${s.problem.pain_points.map(p => `<div class="pain-item">${p}</div>`).join('')}</div>` : ''}
</div>
<div class="section">
<h2>${s.solution?.headline || ''}</h2>
<p>${s.solution?.description || ''}</p>
${s.solution?.key_differentiator ? `<div class="diff">${s.solution.key_differentiator}</div>` : ''}
</div>
<div class="section alt">
<h2>${s.features?.headline || ''}</h2>
<div class="grid">${(s.features?.items || []).map(f => `<div class="card"><h3>${f.title}</h3><p>${f.description}</p></div>`).join('')}</div>
</div>
<div class="section">
<h2>${s.social_proof?.headline || ''}</h2>
${s.social_proof?.testimonial ? `<p class="quote">"${s.social_proof.testimonial.quote}"</p><p style="text-align:center;font-weight:600;font-size:14px">${s.social_proof.testimonial.author}</p><p style="text-align:center;font-size:12px;color:#999">${s.social_proof.testimonial.role}</p>` : ''}
${s.social_proof?.stats?.length ? `<div class="stats">${s.social_proof.stats.map(st => `<div class="stat"><div class="val">${st.value}</div><div class="lbl">${st.label}</div></div>`).join('')}</div>` : ''}
</div>
<div class="cta-sec">
<h2>${s.cta?.headline || ''}</h2>
<p>${s.cta?.subheadline || ''}</p>
<a href="#" class="btn">${s.cta?.button_text || 'Get Started'}</a>
${s.cta?.guarantee ? `<p class="guarantee">${s.cta.guarantee}</p>` : ''}
</div>
<div class="section">
<h2 style="margin-bottom:24px">${s.faq?.headline || 'FAQ'}</h2>
<div class="faq">${(s.faq?.items || []).map(f => `<div class="faq-item"><h4>${f.question}</h4><p>${f.answer}</p></div>`).join('')}</div>
</div>
<div class="footer">Built with ScalitOS</div>
</body></html>`;
    const blob = new Blob([html], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${(name || 'launch-page').replace(/[^a-zA-Z0-9]/g, '-').toLowerCase()}.html`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('HTML downloaded');
  };

  return (
    <DashboardLayout>
      <AnimatePresence>
        {!store.hasSeenIntro && <IntroOverlay onStart={() => store.dismissIntro()} />}
        {creditModal && <CreditModal {...creditModal} onCancel={() => setCreditModal(null)} />}
      </AnimatePresence>

      <div data-testid="launch-page-generator" className="space-y-6">
        <BrandSettingsSummary fields={['brand','color','website']} onLoad={handleBrandProfileLoad} />
        {/* Header */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <div className="flex items-center justify-between mb-1">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-[#c8f060]/[0.08] border border-white/[0.07] flex items-center justify-center">
                <Rocket size={20} className="text-[#c8f060]" />
              </div>
              <div>
                <h1 className="text-2xl font-medium font-['Instrument_Serif',serif]">Landing Page</h1>
                <p className="text-zinc-400 text-sm">Create a complete landing page for your business in seconds</p>
              </div>
            </div>
            <button
              onClick={() => { loadSavedPages(); store.setField('showLibrary', !store.showLibrary); }}
              data-testid="open-library-btn"
              className="flex items-center gap-2 px-4 py-2 rounded-xl border border-white/10 text-sm text-zinc-400 hover:text-white hover:bg-white/5 transition-colors"
            >
              <FolderOpen size={16} />
              Saved ({store.savedPages.length})
            </button>
          </div>
        </motion.div>

        {/* Saved Pages Library */}
        <AnimatePresence>
          {store.showLibrary && (
            <motion.div
              initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }}
              className="overflow-hidden"
            >
              <div className="bg-[#141414] border border-white/[0.07] rounded-2xl p-5">
                <div className="flex items-center justify-between mb-4">
                  <p className="text-sm font-medium text-zinc-300">Your Landing Pages</p>
                  <button onClick={() => store.setField('showLibrary', false)} className="p-1.5 rounded-lg hover:bg-white/5">
                    <X size={14} className="text-zinc-500" />
                  </button>
                </div>
                {store.savedPages.length === 0 ? (
                  <p className="text-zinc-600 text-sm text-center py-6">No saved pages yet. Generate your first landing page to see it here.</p>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {store.savedPages.map(p => (
                      <SavedPageCard key={p.page_id} page={p} onLoad={loadPage} onDelete={deletePage} onRename={renamePage} />
                    ))}
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Main Layout */}
        {!store.sections ? (
          /* ═══ Input Form ═══ */
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
              className="lg:col-span-2"
            >
              <div className="bg-[#141414] border border-white/[0.07] rounded-2xl p-6 space-y-5 sticky top-24">
                <p className="text-zinc-500 text-xs">Turn your idea into a landing page in seconds</p>

                <div>
                  <label className="block text-[11px] uppercase tracking-wider font-medium mb-2" style={{ color: 'rgba(245,240,232,0.4)', fontFamily: 'ui-monospace, monospace' }}>What's this page for?</label>
                  <textarea
                    value={store.businessDescription}
                    onChange={e => store.setField('businessDescription', e.target.value)}
                    data-testid="input-business-desc"
                    placeholder="e.g., my new coaching programme, a free consultation offer, my product launch"
                    rows={3}
                    className="w-full bg-zinc-900 border border-white/10 rounded-xl text-white placeholder:text-zinc-600 px-4 py-3 focus:ring-1 focus:ring-[#c8f060]/30 transition-all text-sm resize-none"
                  />
                </div>

                <div>
                  <label className="block text-[11px] uppercase tracking-wider font-medium mb-2" style={{ color: 'rgba(245,240,232,0.4)', fontFamily: 'ui-monospace, monospace' }}>Page goal</label>
                  <div className="flex gap-2 flex-wrap">
                    {[
                      { id: 'leads',    label: 'Collect leads' },
                      { id: 'sales',    label: 'Drive sales' },
                      { id: 'calls',    label: 'Book calls' },
                      { id: 'waitlist', label: 'Build waitlist' },
                      { id: 'launch',   label: 'Announce launch' },
                    ].map(g => (
                      <button key={g.id} onClick={() => setPageGoal(g.id)} data-testid={`page-goal-${g.id}`}
                        className="px-3 py-1.5 rounded-full text-xs font-medium transition-all"
                        style={{
                          background: pageGoal === g.id ? 'rgba(200,240,96,0.08)' : 'rgba(255,255,255,0.03)',
                          border: `1px solid ${pageGoal === g.id ? 'rgba(200,240,96,0.25)' : 'rgba(255,255,255,0.06)'}`,
                          color: pageGoal === g.id ? '#c8f060' : 'rgba(245,240,232,0.55)',
                        }}>
                        {g.label}
                      </button>
                    ))}
                  </div>
                </div>

                <ReferenceMediaUpload
                  label="Add reference media (optional)"
                  note="Upload a hero image, product photo, or headshot for the page."
                  acceptedTypes={['image']}
                  maxFiles={3}
                  maxSizeMB={25}
                />

                <button
                  onClick={() => handleGenerate(5, false)}
                  disabled={store.loading || !isFormValid}
                  data-testid="generate-launch-page-btn"
                  className="w-full bg-[#c8f060] text-black hover:bg-[#d4f57a] transition-all duration-200 rounded-full font-medium py-3 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {store.loading ? (
                    <>
                      <ArrowsClockwise size={18} className="animate-spin" />
                      Building your page…
                    </>
                  ) : (
                    <>
                      <Rocket size={18} weight="fill" />
                      Build my page · 5 credits
                    </>
                  )}
                </button>

                <button onClick={() => setShowAdvanced(v => !v)} data-testid="lp-customise-toggle"
                  className="w-full text-left px-4 py-2.5 rounded-xl text-[11px] font-semibold flex items-center justify-between"
                  style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', color: 'rgba(245,240,232,0.55)' }}>
                  <span style={{ fontFamily: 'ui-monospace, monospace', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                    {showAdvanced ? 'Hide customise' : 'Customise →'}
                  </span>
                  <span style={{ color: '#c8f060' }}>{showAdvanced ? '−' : '+'}</span>
                </button>

                {showAdvanced && (
                  <div className="space-y-4 pt-1">
                    <div>
                      <label className="block text-sm text-zinc-400 mb-1.5">Business Name</label>
                      <input
                        type="text" value={store.businessName}
                        onChange={e => store.setField('businessName', e.target.value)}
                        data-testid="input-business-name"
                        placeholder="e.g., ScalitOS"
                        className="w-full bg-zinc-900 border border-white/10 rounded-xl text-white placeholder:text-zinc-600 px-4 py-3 focus:ring-1 focus:ring-[#c8f060]/30 transition-all text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-zinc-400 mb-1.5">Target Audience</label>
                      <input
                        type="text" value={store.targetAudience}
                        onChange={e => store.setField('targetAudience', e.target.value)}
                        data-testid="input-audience"
                        placeholder="e.g., Solo founders, small business owners"
                        className="w-full bg-zinc-900 border border-white/10 rounded-xl text-white placeholder:text-zinc-600 px-4 py-3 focus:ring-1 focus:ring-[#c8f060]/30 transition-all text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-zinc-400 mb-1.5">Main Offer</label>
                      <input
                        type="text" value={store.mainOffer}
                        onChange={e => store.setField('mainOffer', e.target.value)}
                        data-testid="input-offer"
                        placeholder="e.g., Free 14-day trial, 50% off first month"
                        className="w-full bg-zinc-900 border border-white/10 rounded-xl text-white placeholder:text-zinc-600 px-4 py-3 focus:ring-1 focus:ring-[#c8f060]/30 transition-all text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-zinc-400 mb-1.5">Top 3 Benefits</label>
                      {store.topBenefits.map((b, i) => (
                        <input
                          key={i} type="text" value={b}
                          onChange={e => store.setBenefit(i, e.target.value)}
                          data-testid={`input-benefit-${i}`}
                          placeholder={['e.g., Save 10+ hours per week', 'e.g., Increase conversions by 3x', 'e.g., No technical skills needed'][i]}
                          className="w-full bg-zinc-900 border border-white/10 rounded-xl text-white placeholder:text-zinc-600 px-4 py-3 focus:ring-1 focus:ring-[#c8f060]/30 transition-all text-sm mb-2"
                        />
                      ))}
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-sm text-zinc-400 mb-1.5">Page Type</label>
                        <select
                          value={store.pageType}
                          onChange={e => store.setField('pageType', e.target.value)}
                          data-testid="input-page-type"
                          className="w-full bg-zinc-900 border border-white/10 rounded-xl text-white px-4 py-3 text-sm appearance-none"
                        >
                          {PAGE_TYPES.map(t => (
                            <option key={t.id} value={t.id}>{t.label}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm text-zinc-400 mb-1.5">Tone</label>
                        <select
                          value={store.tone}
                          onChange={e => store.setField('tone', e.target.value)}
                          data-testid="input-tone"
                          className="w-full bg-zinc-900 border border-white/10 rounded-xl text-white px-4 py-3 text-sm appearance-none"
                        >
                          {TONES.map(t => (
                            <option key={t} value={t}>{t}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </motion.div>

            {/* Empty state */}
            <motion.div
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
              className="lg:col-span-3"
            >
              <div className="bg-[#141414] border border-white/[0.07] rounded-2xl p-12 flex flex-col items-center justify-center min-h-[400px]">
                <div className="w-16 h-16 rounded-2xl bg-white/[0.03] border border-white/[0.07] flex items-center justify-center mb-5">
                  <Rocket size={32} className="text-zinc-700" />
                </div>
                <h3 className="text-lg font-medium text-zinc-400 mb-2">Your landing page will appear here</h3>
                <p className="text-sm text-zinc-600 max-w-sm text-center">Fill in your details on the left and click generate to create a complete landing page</p>
              </div>
            </motion.div>
          </div>
        ) : (
          /* ═══ Generated Output ═══ */
          <div className="space-y-4">
            {/* Action Bar */}
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
              className="bg-[#141414] border border-white/[0.07] rounded-2xl p-4 flex flex-col sm:flex-row items-center justify-between gap-3"
            >
              <div className="flex items-center gap-3">
                <button
                  onClick={() => store.reset()}
                  data-testid="back-to-form-btn"
                  className="flex items-center gap-1.5 text-sm text-zinc-400 hover:text-white transition-colors"
                >
                  <ArrowLeft size={16} /> Edit inputs
                </button>
                <span className="text-zinc-700">|</span>
                <p className="text-zinc-500 text-sm">Your landing page is ready</p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={downloadHTML}
                  data-testid="download-html-btn"
                  className="flex items-center gap-1.5 px-4 py-2 rounded-xl border border-white/10 text-sm text-zinc-300 hover:bg-white/5 transition-colors"
                >
                  <DownloadSimple size={14} />
                  Download HTML
                </button>
                {publishedUrl ? (
                  <div className="flex flex-col gap-2">
                    <div className="flex items-center gap-1.5">
                      <a href={publishedUrl} target="_blank" rel="noreferrer"
                        data-testid="live-page-link"
                        className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-medium"
                        style={{ background: 'rgba(34,211,238,0.08)', border: '1px solid rgba(34,211,238,0.2)', color: '#22d3ee' }}>
                        <Eye size={14} /> View Live
                      </a>
                      <button onClick={() => { navigator.clipboard.writeText(publishedUrl); toast.success('Link copied!'); }}
                        data-testid="copy-link-btn"
                        className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-medium"
                        style={{ background: 'rgba(200,240,96,0.06)', border: '1px solid rgba(200,240,96,0.15)', color: '#c8f060' }}>
                        <Copy size={14} /> Copy Link
                      </button>
                      <a href={`https://wa.me/?text=${encodeURIComponent('Check out my page: ' + publishedUrl)}`} target="_blank" rel="noreferrer"
                        className="px-3 py-2 rounded-xl text-sm border border-white/10 text-zinc-400 hover:bg-white/5">
                        WhatsApp
                      </a>
                      <a href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(publishedUrl)}`} target="_blank" rel="noreferrer"
                        className="px-3 py-2 rounded-xl text-sm border border-white/10 text-zinc-400 hover:bg-white/5">
                        LinkedIn
                      </a>
                      <button onClick={unpublishPage}
                        data-testid="unpublish-btn"
                        className="px-3 py-2 rounded-xl text-sm text-red-400 border border-red-400/20 hover:bg-red-400/5">
                        Unpublish
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    onClick={publishPage}
                    disabled={publishing || !store.pageId}
                    data-testid="publish-page-btn"
                    className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-semibold transition-all"
                    style={{ background: '#c8f060', color: '#080808', opacity: (publishing || !store.pageId) ? 0.5 : 1 }}>
                    {publishing ? <ArrowsClockwise size={14} className="animate-spin" /> : <Rocket size={14} />}
                    {publishing ? 'Publishing...' : 'Publish Live'}
                  </button>
                )}
                <button
                  onClick={copyAllSections}
                  data-testid="copy-all-btn"
                  className="flex items-center gap-1.5 px-4 py-2 rounded-xl border border-white/10 text-sm text-zinc-300 hover:bg-white/5 transition-colors"
                >
                  {copiedAll ? <CheckCircle size={14} className="text-[#c8f060]" /> : <Copy size={14} />}
                  {copiedAll ? 'Copied!' : 'Copy All'}
                </button>
                <button
                  onClick={() => handleGenerate(5, true)}
                  disabled={store.loading}
                  data-testid="regen-all-btn"
                  className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-white/5 border border-white/10 text-sm text-zinc-300 hover:bg-white/10 transition-colors disabled:opacity-40"
                >
                  <ArrowsClockwise size={14} className={store.loading ? 'animate-spin' : ''} />
                  Regenerate Page (5 credits)
                </button>
              </div>
            </motion.div>

            {/* Tabs */}
            <div className="flex gap-1 bg-[#141414] border border-white/[0.07] rounded-xl p-1 w-fit flex-wrap">
              <button
                onClick={() => store.setTab('copy')}
                data-testid="tab-copy"
                className={`flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-medium transition-colors ${
                  store.activeTab === 'copy' ? 'bg-white/10 text-white' : 'text-zinc-500 hover:text-white'
                }`}
              >
                <TextAa size={16} /> Copy
              </button>
              <button
                onClick={() => store.setTab('edit')}
                data-testid="tab-edit"
                className={`flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-medium transition-colors ${
                  store.activeTab === 'edit' ? 'bg-white/10 text-white' : 'text-zinc-500 hover:text-white'
                }`}
              >
                <PencilSimple size={16} /> Edit & Publish
              </button>
              <button
                onClick={() => store.setTab('preview')}
                data-testid="tab-preview"
                className={`flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-medium transition-colors ${
                  store.activeTab === 'preview' ? 'bg-white/10 text-white' : 'text-zinc-500 hover:text-white'
                }`}
              >
                <Eye size={16} /> Preview
              </button>
            </div>

            {/* Tab Content */}
            <AnimatePresence mode="wait">
              {store.activeTab === 'copy' ? (
                <motion.div
                  key="copy" initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 10 }}
                  className="space-y-4"
                >
                  {Object.keys(SECTION_LABELS).map((key) => (
                    <SectionCopyCard
                      key={key}
                      sectionKey={key}
                      content={store.sections?.[key]}
                      onRegenerate={() => handleRegenerateSection(key)}
                      isRegenerating={store.regeneratingSection === key}
                    />
                  ))}
                </motion.div>
              ) : store.activeTab === 'edit' ? (
                <motion.div key="edit" initial={{ opacity: 0, x: 0 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 0 }}>
                  <LaunchPageEditor
                    sections={store.sections}
                    pageId={store.pageId}
                    onChange={(next) => store.setSections(next, store.pageId)}
                    onPublished={(data) => { if (data?.url || data?.published_url) setPublishedUrl(data.url || data.published_url); loadSavedPages(); }}
                  />
                </motion.div>
              ) : (
                <motion.div
                  key="preview" initial={{ opacity: 0, x: 10 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -10 }}
                >
                  <div className="bg-[#141414] border border-white/[0.07] rounded-2xl p-4 sm:p-8">
                    <p className="text-center text-zinc-500 text-xs mb-6">This is how your page could look</p>
                    <PreviewTab sections={store.sections} />
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default LaunchPageGenerator;
